import _ from 'lodash'
import React from 'react'
import classNames from 'classnames'

import apis from 'browser/app/models/apis'
import { LoadingSpinner } from 'browser/components/atomic-elements/atoms/loading-spinner/loading-spinner'
import { Popover } from 'browser/components/atomic-elements/atoms/popover/popover'
import { IComponentsContext } from 'browser/contexts/components/components-context'
import MobileComponents from '../index'
import { ViewRenderer } from 'shared-libs/components/view/renderer'
import { MultiMixinRenderer } from 'shared-libs/components/entity/multi-mixin-renderer'
import { Entity } from 'shared-libs/models/entity'
import { Query } from 'shared-libs/models/query'
import './_mobile-entity-search-field.scss'
import { globalTranslationTable } from 'browser/mobile/util/global-translation-table'
import { translateString } from 'shared-libs/helpers/utils'

import {
  DEFAULT_SEARCH_ORDER,
  IMobileEntitySearchFieldProps,
  IResolvedSchemaPaths,
  ISelectionPayload,
  NUM_SEARCH_RESULTS_TO_SHOW,
} from './_utils'

interface IMobileEntitySearchFieldSubmitResultsOverlayProps {
  fieldProps: IMobileEntitySearchFieldProps
  componentsContext: IComponentsContext
  entitySchema: any
  entityDisplayName: string
  schemaPaths: IResolvedSchemaPaths
  searchFilter: string
  onCommitSelection: (payload: ISelectionPayload) => void
  onCommitRawValue?: (searchFilter: string) => void
  onSubmitSearch?: (hasResults: boolean) => void
  onClose?: () => void
}

interface IMobileEntitySearchFieldSubmitResultsOverlayState {
  entities: Entity[]
  isSearching: boolean
  selectedItem?: Entity
}

export class MobileEntitySearchFieldSubmitResultsOverlay extends React.Component<
  IMobileEntitySearchFieldSubmitResultsOverlayProps,
  IMobileEntitySearchFieldSubmitResultsOverlayState
> {
  private collection: any

  constructor(props: IMobileEntitySearchFieldSubmitResultsOverlayProps) {
    super(props)

    this.collection = undefined
    this.state = {
      entities: [],
      isSearching: false,
      selectedItem: undefined,
    }
  }

  public componentDidMount(): void {
    this.runQuery(this.props.searchFilter)
  }

  public componentDidUpdate(prevProps: IMobileEntitySearchFieldSubmitResultsOverlayProps): void {
    if (prevProps.searchFilter !== this.props.searchFilter) {
      this.runQuery(this.props.searchFilter)
    }
  }

  public componentWillUnmount(): void {
    this.collection?.cancel()
    this.collection?.dispose()
  }

  public render() {
    const { isSearching, entities } = this.state

    return (
      <Popover className="entity-search__submit-results-popover">
        <div className="entity-search__submit-results-content">
          {isSearching ? (
            <div className="pt3 pb3">
              <LoadingSpinner />
            </div>
          ) : _.isEmpty(entities) ? (
            <>
              {this.renderEmptyResultState()}
              {this.renderRawSubmitOption()}
            </>
          ) : (
            <>
              <div className="entity-search__submit-results-header">
                {translateString('Entity_Search_Results_For', globalTranslationTable, { searchFilter: this.props.searchFilter })}
              </div>
              {_.map(entities, (entity, index) => this.renderResultCard(entity, index))}
              {this.renderRawSubmitOption()}
            </>
          )}
        </div>
      </Popover>
    )
  }

  private renderRawSubmitOption = () => {
    const { fieldProps, searchFilter } = this.props
    if (!fieldProps.rawQueryPath || _.isEmpty(_.trim(searchFilter))) {
      return null
    }
    const trimmedSearchFilter = _.trim(searchFilter)
    const label = fieldProps.rawSubmitLabel
      ? fieldProps.rawSubmitLabel.replace('{searchFilter}', trimmedSearchFilter)
      : `Not listed? Use "${trimmedSearchFilter}" instead`
    return (
      <div className="entity-search__raw-submit-item" onClick={() => this.handleRawSubmit(trimmedSearchFilter)}>
        {label}
      </div>
    )
  }

  private handleRawSubmit = (trimmedSearchFilter: string) => {
    this.props.onCommitRawValue?.(trimmedSearchFilter)
    this.props.onClose?.()
  }

  private runQuery = (searchFilter: string) => {
    if (_.isEmpty(searchFilter)) {
      this.setState({ entities: [], isSearching: false })
      return
    }
    this.queryEntities(searchFilter)
  }

  private renderEmptyResultState = () => {
    const { emptyResult } = this.props.schemaPaths

    if (emptyResult && _.get(this.props.entitySchema, emptyResult)) {
      return (
        <ViewRenderer
          schema={_.get(this.props.entitySchema, emptyResult)}
          state={this.getRendererState()}
          componentsMap={MobileComponents}
          api={apis}
        />
      )
    }

    return <div className="pa3 tc">{translateString('Entity_Search_No_Results', globalTranslationTable, { searchFilter: this.props.searchFilter })}</div>
  }

  private renderResultCard = (entity: Entity, index: number) => {
    const { selectedItem } = this.state
    const { item } = this.props.schemaPaths
    const { fieldProps } = this.props

    const selectOption = () => this.handleSelectEntity(entity)
    const isSelected = selectedItem?.uniqueId === entity?.uniqueId

    let content: React.ReactNode

    if (fieldProps.renderItem) {
      content = fieldProps.renderItem({
        entity,
        item: entity,
        selectedItem,
        selectOption,
      })
    } else if (item && _.get(this.props.entitySchema, item)) {
      content = (
        <MultiMixinRenderer
          actions={this}
          componentsMap={MobileComponents}
          context={{
            density: 'collapse',
            isHorizontalLayout: false,
            isStatic: true,
          }}
          state={this.getRendererState()}
          value={entity}
          uiSchemaPath={item}
          shouldOverrideUiSchema={true}
        />
      )
    } else {
      content = (
        <div className="entity-search__result-card-fallback-label">
          {entity?.displayName}
        </div>
      )
    }

    return (
      <div
        key={entity?.uniqueId || index}
        onClick={selectOption}
        className={classNames('entity-search__result-card', {
          'entity-search__result-card--selected': isSelected,
        })}
      >
        {content}
      </div>
    )
  }

  private handleSelectEntity = async (entity: Entity) => {
    this.setState({ selectedItem: entity })

    const refreshedItem = await this.refreshEntity(entity)

    this.props.onCommitSelection({
      entity: refreshedItem,
      searchFilter: this.props.searchFilter,
    })
    this.props.onClose?.()
  }

  private queryEntities = async (searchFilter: string) => {
    const { query = {}, transformSearchFilterFn, transformResultsFn } = this.props.fieldProps
    const { entityType, queryPath, queryPaths, queryType } = query

    this.setState({ isSearching: true, selectedItem: undefined })

    if (!entityType) {
      this.setState({ entities: [], isSearching: false })
      return
    }

    const searchQuery = transformSearchFilterFn
      ? transformSearchFilterFn(searchFilter)
      : searchFilter

    const queryBuilder = new Query(apis)
      .setEntityType(entityType)
      .setQueryType(queryType)
      .setComputations(query?.computations)
      .setGroups(query?.groups)
      .setFilters(query?.filters)
      .setMetadata(query?.metadata)
      .setSize(NUM_SEARCH_RESULTS_TO_SHOW)
      .setOrders(query?.orders || DEFAULT_SEARCH_ORDER)
      .setQuery(searchQuery)

    if (queryPaths) {
      queryBuilder.setQueryPaths(queryPaths)
    } else {
      queryBuilder.setQueryPath(queryPath)
    }

    this.collection?.cancel()
    this.collection?.dispose()

    this.collection = queryBuilder.getCollection()

    try {
      let entities = await this.collection.find()

      if (transformResultsFn) {
        // transformResultsFn may return a Promise (e.g. distance sort via GEOPOSITION()).
        entities = await Promise.resolve(transformResultsFn({ entities, searchQuery }))
      }

      this.props.onSubmitSearch?.(!_.isEmpty(entities))

      this.setState({
        entities
      })
    } catch (e) {
      if (this.isCancellationError(e)) {
        return
      }

      console.error('entity-search submit query failed', {
        error: e,
        searchFilter,
      })

      this.props.onSubmitSearch?.(false)
      this.setState({ entities: [] })
    } finally {
      this.collection = undefined
      this.setState({ isSearching: false })
    }
  }

  private refreshEntity = async (entity: Entity) => {
    if (_.isNil(entity?.uniqueId)) {
      return entity
    }

    try {
      return await apis.getStore().findRecord(entity.uniqueId)
    } catch (e) {
      return entity
    }
  }

  private isCancellationError = (error: any) => {
    return (
      error?.name === 'CancellationError' ||
      error?.message?.includes('canceled') ||
      error?.message?.includes('cancelled') ||
      error?.message?.includes('aborted')
    )
  }

  private getRendererState = () => {
    return {
      ...this.props.componentsContext,
      ...this.props.fieldProps,
      entities: this.state.entities,
      searchFilter: this.props.searchFilter,
      entityDisplayName: this.props.entityDisplayName,
    }
  }
}
